import type { QuizSectionSchema } from '@repo/schemas/quiz/quizSection';

import { cn } from '@repo/lib/cn';
import { hms } from '@repo/lib/time/timeUtils';
import { CopyCheckIcon, CopyIcon, DotIcon, GripVerticalIcon, PencilIcon, TrashIcon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { SortableItemHandler } from '@/components/ui/dnd';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface QuizBuilderSectionCardHeaderProps {
  section: QuizSectionSchema;
  sectionIndex: number;
  onDelete: (sectionIndex: number) => void;
  onDuplicate: (sectionIndex: number) => void;
}

export default function QuizBuilderSectionCardHeader(quizBuilderSectionCardHeaderProps: QuizBuilderSectionCardHeaderProps) {
  const { section, sectionIndex, onDelete, onDuplicate } = quizBuilderSectionCardHeaderProps;
  const [copied, setCopied] = useState<boolean>(false);

  const handleDuplicate = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (copied) return;

    setCopied(true);
    onDuplicate(sectionIndex);
    window.setTimeout(() => setCopied(false), 850);
  };

  return (
    <div className="flex h-8 items-center gap-1">
      <SortableItemHandler>
        <Button type="button" variant={'ghost'} size={'sm'} className="h-8 min-h-8 w-6 min-w-6">
          <GripVerticalIcon />
        </Button>
      </SortableItemHandler>
      <span className="text-muted-foreground truncate text-xs font-medium">{section.name}</span>
      <DotIcon className="text-muted-foreground size-4 shrink-0" />
      {section.time ? <span className="text-muted-foreground truncate text-xs font-medium">{hms(section.time)}</span> : <span className="text-muted-foreground text-xs">Brak limitu czasu</span>}

      <div className="ml-auto flex gap-0.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button onClick={() => onDelete(sectionIndex)} variant={'ghost'} type="button" size={'sm'} className="size-8 min-h-8 min-w-8">
              <TrashIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Usuń sekcję</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button type="button" variant={'ghost'} size={'sm'} className="relative size-8 min-h-8 min-w-8" onClick={handleDuplicate} disabled={copied}>
              <div className={cn('transition-all', copied ? 'scale-100 opacity-100' : 'scale-0 opacity-0')}>
                <CopyCheckIcon className="text-text-success" aria-hidden="true" />
              </div>
              <div className={cn('absolute transition-all', copied ? 'scale-0 opacity-0' : 'scale-100 opacity-100')}>
                <CopyIcon aria-hidden="true" className="text-text-tertiary" />
              </div>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Duplikuj sekcję</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant={'ghost'} type="button" size={'sm'} className="size-8 min-h-8 min-w-8" asChild>
              <Link to={`section/${sectionIndex}`}>
                <PencilIcon />
              </Link>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Edytuj sekcję</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
